// frontend/src/pages/manufacturing/v2/TransactionHistoryV2.jsx
//
// Global v2 transaction ledger — a paginated, filterable view over every
// material movement recorded by the v2 module. Backed by GET /api/v2/history/.
//
// Filters (all server-side): item-code substring (search box), WO number,
// CO number, movement-type multi-select, user, and a from/to date range.
// Text filters are debounced (400ms) so typing doesn't hammer the endpoint —
// same pattern as the shop-floor search bars.
//
// Clicking a WO number opens the WOv2DetailDrawer (which lazily loads its own
// History tab), so this page doubles as a jump-off point into per-WO detail.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  LinearProgress, Alert, Link, Stack, Checkbox, ListItemText, OutlinedInput,
  InputLabel, FormControl, Select, InputAdornment,
} from '@mui/material';
import { Refresh, Search, History as HistoryIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { v2 } from '../../../services/api';
import usePageTitle from '../../../hooks/usePageTitle';
import GenerationModeBadge from './components/GenerationModeBadge';
import WOv2DetailDrawer from './components/WOv2DetailDrawer';
import {
  MOVEMENT_TYPE_OPTIONS, movementTypeColor, movementTypeLabel, signedQuantity,
} from './utils/movementDisplay';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 400;

// Filter inputs survive menu switches: the page unmounts on navigation, so
// state alone loses whatever was typed. sessionStorage (not localStorage)
// keeps it for the browser session without stale filters days later.
const FILTERS_KEY = 'wo_v2_txn_history_filters';

function readStoredFilters() {
  try {
    return JSON.parse(sessionStorage.getItem(FILTERS_KEY)) || {};
  } catch {
    return {};
  }
}

function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function TransactionHistoryV2() {
  usePageTitle('Transaction History V2');
  // Filter inputs — text filters are debounced into `debounced` below.
  // Restored from sessionStorage so switching menus keeps what was typed.
  const [search, setSearch] = useState(() => readStoredFilters().search || '');          // item code substring
  const [woNumber, setWoNumber] = useState(() => readStoredFilters().woNumber || '');
  const [coNumber, setCoNumber] = useState(() => readStoredFilters().coNumber || '');
  const [createdBy, setCreatedBy] = useState(() => readStoredFilters().createdBy || '');
  const [movementTypes, setMovementTypes] = useState(() => readStoredFilters().movementTypes || []);
  const [dateFrom, setDateFrom] = useState(() => readStoredFilters().dateFrom || '');
  const [dateTo, setDateTo] = useState(() => readStoredFilters().dateTo || '');

  // Seed the debounced values from storage too, so the first fetch after a
  // remount is already filtered (no unfiltered flash + refetch).
  const [debounced, setDebounced] = useState(() => {
    const s = readStoredFilters();
    return {
      search: (s.search || '').trim(),
      woNumber: (s.woNumber || '').trim(),
      coNumber: (s.coNumber || '').trim(),
      createdBy: (s.createdBy || '').trim(),
    };
  });

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerWoId, setDrawerWoId] = useState(null);

  const loadingRef = useRef(false);

  // Debounce the free-text filters.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced({
        search: search.trim(),
        woNumber: woNumber.trim(),
        coNumber: coNumber.trim(),
        createdBy: createdBy.trim(),
      });
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search, woNumber, coNumber, createdBy]);

  // Non-text filters reset to page 1 immediately.
  useEffect(() => { setPage(1); }, [movementTypes, dateFrom, dateTo]);

  // Persist every filter input so navigation away and back restores them.
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify({
        search, woNumber, coNumber, createdBy, movementTypes, dateFrom, dateTo,
      }));
    } catch { /* storage unavailable - keeping filters is best-effort */ }
  }, [search, woNumber, coNumber, createdBy, movementTypes, dateFrom, dateTo]);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (debounced.search) p.item = debounced.search;
    if (debounced.woNumber) p.work_order = debounced.woNumber;
    if (debounced.coNumber) p.customer_order_number = debounced.coNumber;
    if (debounced.createdBy) p.created_by = debounced.createdBy;
    if (movementTypes.length > 0) p.movement_type = movementTypes.join(',');
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [page, debounced, movementTypes, dateFrom, dateTo]);

  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await v2.history.list(params);
      const data = res.data || {};
      // DRF pagination shape: { count, results }. Fall back to a bare array.
      setRows(Array.isArray(data) ? data : (data.results || []));
      setCount(Array.isArray(data) ? data.length : (data.count ?? 0));
    } catch (err) {
      console.error('Failed to load transaction history', err);
      setError(err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setSearch(''); setWoNumber(''); setCoNumber(''); setCreatedBy('');
    setMovementTypes([]); setDateFrom(''); setDateTo('');
  };

  const hasActiveFilters = search || woNumber || coNumber || createdBy
    || movementTypes.length > 0 || dateFrom || dateTo;

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const fromRow = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toRow = Math.min(count, page * PAGE_SIZE);

  const handleRefresh = () => {
    load().then(() => toast.success('Refreshed')).catch(() => {});
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Card sx={{ mb: 3, border: '2px solid #1976d2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              Transaction History
              <GenerationModeBadge />
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Every material movement recorded by the v2 module, newest first.
          </Typography>
        </CardContent>
      </Card>

      {/* Filter bar */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search item code"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="e.g. GLU-01"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                  ),
                }}
                inputProps={{ 'data-testid': 'filter-item' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Work order #"
                size="small"
                fullWidth
                value={woNumber}
                onChange={(e) => setWoNumber(e.target.value)}
                inputProps={{ 'data-testid': 'filter-wo-number' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Customer order #"
                size="small"
                fullWidth
                value={coNumber}
                onChange={(e) => setCoNumber(e.target.value)}
                inputProps={{ 'data-testid': 'filter-co-number' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="User"
                size="small"
                fullWidth
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                placeholder="username"
                inputProps={{ 'data-testid': 'filter-user' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel id="movement-type-label">Movement type</InputLabel>
                <Select
                  labelId="movement-type-label"
                  multiple
                  value={movementTypes}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMovementTypes(typeof v === 'string' ? v.split(',') : v);
                  }}
                  input={<OutlinedInput label="Movement type" />}
                  renderValue={(selected) => selected.map(movementTypeLabel).join(', ')}
                  inputProps={{ 'data-testid': 'filter-movement-type' }}
                >
                  {MOVEMENT_TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Checkbox checked={movementTypes.indexOf(t) > -1} size="small" />
                      <ListItemText primary={movementTypeLabel(t)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date from"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                inputProps={{ 'data-testid': 'filter-date-from' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date to"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                inputProps={{ 'data-testid': 'filter-date-to' }}
              />
            </Grid>
            {hasActiveFilters && (
              <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={clearFilters}
                  data-testid="clear-filters"
                >
                  Clear filters
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && <LinearProgress data-testid="loading-state" />}
          {error && !loading && (
            <Alert severity="error" sx={{ my: 2 }} data-testid="error-state">
              Failed to load transaction history.
              <Button onClick={load} size="small" sx={{ ml: 1 }}>Retry</Button>
            </Alert>
          )}
          {!loading && !error && rows.length === 0 && (
            <Alert severity="info" sx={{ my: 2 }} data-testid="empty-state">
              <Typography variant="h6">No transactions found</Typography>
              <Typography variant="body2">
                {hasActiveFilters
                  ? 'Try adjusting or clearing your filters.'
                  : 'No material movements have been recorded yet.'}
              </Typography>
            </Alert>
          )}
          {!loading && !error && rows.length > 0 && (
            <>
              <TableContainer>
                <Table size="small" data-testid="history-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Work Order</TableCell>
                      <TableCell>Customer Order #</TableCell>
                      <TableCell>User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover data-testid={`history-row-${row.id}`}>
                        <TableCell>
                          <Typography variant="body2">{formatTimestamp(row.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={movementTypeLabel(row.movement_type)}
                            color={movementTypeColor(row.movement_type)}
                            data-testid={`movement-chip-${row.id}`}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                            data-testid={`qty-${row.id}`}
                          >
                            {signedQuantity(row)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{row.item_code}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.item_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.wo_number ? (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => row.work_order_id != null && setDrawerWoId(row.work_order_id)}
                              data-testid={`wo-link-${row.id}`}
                            >
                              {row.wo_number}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.customer_order_number || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.created_by_username || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Server-side pagination */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {fromRow}–{toRow} of {count}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="page-prev"
                >
                  Previous
                </Button>
                <Typography variant="body2" data-testid="page-indicator">
                  Page {page} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="page-next"
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <WOv2DetailDrawer
        open={drawerWoId != null}
        workOrderId={drawerWoId}
        onClose={() => setDrawerWoId(null)}
      />
    </Box>
  );
}
